import { forwardRef, type ComponentProps, type SyntheticEvent, useCallback } from 'react';
import { useHandleImageError } from '@pkgs/components/hooks/useHandleImageError';
import { DEFAULT_IMAGE } from '@pkgs/api';
import { datadogRum } from '@datadog/browser-rum';

import { ImageComponent } from './ImageComponent';

type Props = ComponentProps<typeof ImageComponent> & {
  fallback?: string;
};

export const ImageWithFallBack = forwardRef<HTMLImageElement, Props>(
  ({ src, fallback = DEFAULT_IMAGE, className, onError: onErrorProp, ...props }, ref) => {
    const onError = useCallback(
      (e: SyntheticEvent<HTMLImageElement, Event>) => {
        onErrorProp?.(e);
        datadogRum.addError(e, { extra: { message: `Failed to load image from ${src}` } });
      },
      [onErrorProp, src],
    );

    const { handleImageError, isError } = useHandleImageError(src, fallback, onError);

    return <ImageComponent ref={ref} {...props} className={className} src={isError ? fallback : src} onError={handleImageError} />;
  },
);

ImageWithFallBack.displayName = 'ImageWithFallBack';
